import { Button, ButtonGroup, Flex, ScrollView } from "@aws-amplify/ui-react"
import type { CognitoUserDataDto, UserDataDto } from "@strtmn/sds/types"
import { DEFAULT_USER_ID } from "@strtmn/sds/users"
import { getErrorMessage } from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { listCognitoUsers, listUsers } from "../../api/userData"
import { LoadingErrorState, RoundedCard } from "../../components"
import UserTable from "../../components/admin/UserTable"
import SearchField from "../../components/maschinen-liste/SearchField"
import { isProd } from "../../config"

export function AdminCognitoUsers() {
  const [users, setUsers] = useState<CognitoUserDataDto[] | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | undefined>(undefined)

  useEffect(() => {
    const loadData = async () => {
      setLoading(true)
      setUsers(null)
      setError(undefined)
      try {
        const [_users, _cognitoUsers] = await Promise.all([
          listUsers(),
          listCognitoUsers(),
        ])
        if (!_users) {
          throw new Error("Could not load users")
        }
        if (!_cognitoUsers) {
          throw new Error("Could not load cognito users")
        }
        !isProd && console.debug("ACU.loadData: _users =", _users)
        !isProd && console.debug("ACU.loadData: _cognitoUsers =", _cognitoUsers)
        const _userIds = _users.map((u) => u.id)
        const _newCognitoUsers = _cognitoUsers.filter(
          (cu) => !_userIds.includes(cu.id)
        )
        !isProd &&
          console.debug("ACU.loadData: _newCognitoUsers =", _newCognitoUsers)
        const pushU1ToBack = 1
        const pushU2ToBack = -1
        _newCognitoUsers.sort((u1, u2) => {
          if (u1.created_at !== u2.created_at) {
            return u1.created_at < u2.created_at ? pushU1ToBack : pushU2ToBack
          }
          return 0
        })
        setUsers(_newCognitoUsers)
      } catch (err) {
        console.log(
          "ACU.loadData: Caught an error:",
          JSON.stringify(err, null, 2)
        )
        setError(getErrorMessage(err))
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  if (loading || error) {
    /** @todo add white text color */
    return (
      <LoadingErrorState
        isLoading={loading}
        loadingMessage={I18n.get("Lade Benutzerdaten, bitte warten...")}
        isError={!!error}
        errorMessage={`${I18n.get("Fehler")}: ${I18n.get(error!)}`}
      />
    )
  }

  !isProd && console.debug("ACU: users =", users)
  const loadedUsers = users!.map((cu) => {
    const u: UserDataDto = {
      id: cu.id,
      email: cu.email!,
      first_name: "n/a",
      last_name: "n/a",
      created_at: cu.created_at,
      created_by: DEFAULT_USER_ID,
      updated_at: cu.updated_at ?? cu.created_at,
      updated_by: DEFAULT_USER_ID,
      Organizations: [],
    }
    return u
  })

  return (
    <main className="p-8">
      <Flex
        className="!mb-6"
        justifyContent="space-between"
        alignItems="center"
        alignContent="center"
      >
        <div className="page-heading">{I18n.get("Benutzer anlegen")}</div>
        <SearchField whiteBackground={true} />
      </Flex>

      <RoundedCard>
        <ScrollView
          width="100%"
          height="calc(100vh - 400px)" // or any desired height
        >
          <UserTable users={loadedUsers} newUsers={true} />
        </ScrollView>

        <ButtonGroup size="small">
          <Link to="..">
            <Button variation="link" size="small">
              {I18n.get("Zurück")}
            </Button>
          </Link>
        </ButtonGroup>
      </RoundedCard>
    </main>
  )
}
